import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Calculator,
  Layers,
  PackageSearch,
  RefreshCw,
  Settings2,
} from "lucide-react";
import StockManagerLayout from "../components/StockManagerLayout";
import { API_BASE_URL } from "../utils/apiConfig";
import { calculatePackPricingFromRow } from "../utils/packPricing";

const apiUrl = (path) => `${API_BASE_URL}${path}`;

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const wholeMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.ceil(Number(value || 0)));

const percent = (value) => `${Number(value || 0).toFixed(2)}%`;

const quantity = (value) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
};

const dimensions = [
  { key: "product", label: "Product-wise" },
  { key: "pack", label: "Pack-wise" },
  { key: "purchase", label: "Purchase-wise" },
  { key: "batch", label: "Batch-wise" },
  { key: "supplier", label: "Supplier-wise" },
  { key: "outlet", label: "Outlet-wise" },
  { key: "ecommerce", label: "Ecommerce-wise" },
];

const StatCard = ({ label, value, detail, icon: Icon }) => (
  <div className="rounded-lg border bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-semibold text-gray-500">{label}</div>
        <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
        {detail ? <div className="mt-1 text-xs text-gray-500">{detail}</div> : null}
      </div>
      {Icon ? (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
          <Icon size={20} />
        </div>
      ) : null}
    </div>
  </div>
);

const DetailBox = ({ title, rows, renderRow, emptyText }) => (
  <div className="rounded-lg border bg-white p-3">
    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
      {title}
    </div>
    {rows.length ? (
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="text-sm text-gray-700">
            {renderRow(row)}
          </div>
        ))}
      </div>
    ) : (
      <div className="text-sm text-gray-500">{emptyText}</div>
    )}
  </div>
);

const asArray = (value) => (Array.isArray(value) ? value : []);
const rowIdentity = (row) => String(row.key || row.label || "");
const productIdentityText = (row) =>
  [
    row.brand_name ? `Brand: ${row.brand_name}` : "",
    row.mk_barcode ? `MK: ${row.mk_barcode}` : "",
    row.vendor_barcode ? `Vendor: ${row.vendor_barcode}` : "",
  ].filter(Boolean).join(" | ");

const productPackDisplayName = (row) => {
  const productName = [row.brand_name, row.product_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const packName =
    row.pack_name ||
    [row.pack_quantity, row.pack_unit].filter(Boolean).join(" ").trim();

  if (productName && packName) return `${productName} - ${packName}`;
  if (productName) return productName;
  if (packName) return packName;
  return row.label || row.key || "-";
};

const rowContextText = (row) => {
  const parts = [];
  if (row.purchase_order_id) parts.push(`Purchase #${row.purchase_order_id}`);
  if (row.batch_id) parts.push(`Batch ${row.batch_id}`);
  const identity = productIdentityText(row);
  if (identity) parts.push(identity);
  return parts.join(" | ");
};

const isFullySold = (row) => {
  const sold = Number(row.quantity_sold || 0);
  const purchased = Number(row.no_of_units || 0);
  return purchased > 0 && sold >= purchased;
};

const resultLabel = (row) => (isFullySold(row) ? "Profit/Loss" : "Earned until now");

const costPerPiece = (row) => {
  return calculatePackPricingFromRow(row).costPerPiece;
};

const dpricePerPiece = (row) => {
  return calculatePackPricingFromRow(row).dprice;
};

const pricePerPiece = (row) => {
  return calculatePackPricingFromRow(row).mrp;
};

const FinancialRowDetails = ({ row, extraCost, profit, loadingDetails }) => {
  const dpriceTotal =
    calculatePackPricingFromRow(row).dpriceTotal;
  const marginAmount = dpriceTotal - Number(row.final_cost || 0);
  const rowCostPerPiece = costPerPiece(row);
  const rowPricePerPiece = pricePerPiece(row);
  const rowFinalPricePerPiece = dpricePerPiece(row);
  const purchaseRows = asArray(row.purchase_details).length
    ? asArray(row.purchase_details)
    : Number(row.purchase_cost || 0) > 0
      ? [{
          purchase_order_id: row.purchase_order_id,
          inventory_product_id: row.inventory_product_id,
          product_barcode_id: row.product_barcode_id,
          batch_id: row.batch_id,
          pack_name: row.pack_name,
          pack_quantity: row.pack_quantity,
          pack_unit: row.pack_unit,
          brand_name: row.brand_name,
          mk_barcode: row.mk_barcode,
          vendor_barcode: row.vendor_barcode,
          no_of_units: row.no_of_units,
          purchase_unit_price: row.purchase_unit_price,
          base_purchase_cost: row.purchase_cost,
          label: row.label,
        }]
      : [];

  const detailedExtraRows = [
    ...asArray(row.purchase_extra_details),
    ...asArray(row.inventory_extra_details),
  ];
  const extraRows = detailedExtraRows.length
    ? detailedExtraRows
    : extraCost > 0
      ? [{
          cost_head: loadingDetails ? "Loading configured extra costs" : "Extra Cost Total",
          allocation_method: loadingDetails ? "Please wait" : "Configured during verification",
          allocated_amount: extraCost,
          gst_amount: 0,
          total_amount: extraCost,
          notes: loadingDetails
            ? "Fetching the cost heads selected during verification."
            : "Summary of purchase/inventory extra costs for this row.",
        }]
      : [];

  return (
    <div className="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">
      <DetailBox
        title="Purchase Parameters"
        rows={purchaseRows}
        emptyText="No purchase amount found for this row"
        renderRow={(item) => (
          <div className="space-y-1">
            <div className="font-semibold text-gray-900">
              {productPackDisplayName(item)}
            </div>
            <div>
              PO #{item.purchase_order_id || "-"} | Batch {item.batch_id || "-"}
            </div>
            <div>
              Source: {item.cost_source === "INTERNAL_PACKING" ? "Internal packing output" : "Purchase"}
            </div>
            <div>
              {productIdentityText(item) || "Brand/barcode: -"}
            </div>
            <div>
              Pack: {item.pack_name || [
                item.pack_quantity,
                item.pack_unit,
              ].filter(Boolean).join(" ") || "-"}
            </div>
            <div>
              Units: {item.no_of_units ? Number(item.no_of_units || 0).toFixed(2) : "-"}
              {item.purchase_unit_price ? ` x ${wholeMoney(item.purchase_unit_price)}` : ""}
            </div>
            <div>
              Outlet received: {item.outlet_received_units ? quantity(item.outlet_received_units) : "0"}
            </div>
            <div>
              Sold from this purchase: {quantity(item.quantity_sold)} | Revenue: {money(item.revenue)}
            </div>
            <div>Base purchase: {money(item.base_purchase_cost)}</div>
          </div>
        )}
      />
      <DetailBox
        title="Extra Cost Parameters"
        rows={extraRows}
        emptyText="No extra costs configured for this row"
        renderRow={(item) => (
          <div className="space-y-1">
            <div className="font-semibold text-gray-900">
              {item.cost_head || "Extra Cost"} ({item.allocation_method || "-"})
            </div>
            <div>
              Cost {money(item.allocated_amount)} + GST {money(item.gst_amount)} = {money(item.total_amount)}
            </div>
            {item.notes &&
            !String(item.notes).toLowerCase().includes("purchase verification") ? (
              <div className="text-xs text-gray-500">{item.notes}</div>
            ) : null}
          </div>
        )}
      />
      <DetailBox
        title="Margin Parameters"
        rows={[row]}
        emptyText="No margin configured"
        renderRow={(item) => (
          <div className="space-y-1">
            <div>Final cost: {money(item.final_cost)}</div>
            <div>Config margin: {percent(item.configured_margin_percent)}</div>
            {asArray(item.purchase_details).some((detail) => detail.proportion_factor) ? (
              <div>
                Factor: {Number(asArray(item.purchase_details)[0]?.proportion_factor || 1).toFixed(3)}
              </div>
            ) : null}
            <div>Final margin: {percent(item.rounded_margin_percent)}</div>
            <div>Margin amount: {money(marginAmount)}</div>
            <div className="font-semibold text-gray-900">
              MRP total: {money(rowPricePerPiece * Number(item.no_of_units || 0))}
            </div>
            <div>MRP / piece: {wholeMoney(rowPricePerPiece)}</div>
            <div>Discount: {percent(item.configured_discount_percent)}</div>
            <div className="font-bold text-gray-900">
              D.Price / piece after discount: {wholeMoney(rowFinalPricePerPiece)}
            </div>
          </div>
        )}
      />
      <DetailBox
        title="Result"
        rows={[row]}
        emptyText="No result"
        renderRow={(item) => (
          <div className="space-y-1">
            <div>Purchase: {money(item.purchase_cost)}</div>
            <div>Extra cost: {money(extraCost)}</div>
            <div className="font-semibold text-gray-900">
              Final cost: {money(item.final_cost)}
            </div>
            <div>Cost / piece: {wholeMoney(rowCostPerPiece)}</div>
            <div>Margin amount: {money(marginAmount)}</div>
            <div className="font-semibold text-gray-900">
              Purchase + extra cost + margin: {money(dpriceTotal)}
            </div>
            <div>MRP / piece: {wholeMoney(rowPricePerPiece)}</div>
            <div className="font-bold text-gray-900">
              D.Price / piece after discount: {wholeMoney(rowFinalPricePerPiece)}
            </div>
            <div>Pieces sold: {quantity(item.quantity_sold)}</div>
            <div>Revenue: {money(item.revenue)}</div>
            <div className={`font-bold ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>
              {resultLabel(item)}: {money(profit)}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default function ProductPackFinancialsPage() {
  const token = useSelector((state) => state.posUser?.userInfo?.token || "");
  const [activeDimension, setActiveDimension] = useState("pack");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState([]);
  const [expandedRow, setExpandedRow] = useState("");
  const [detailLoadingRows, setDetailLoadingRows] = useState({});
  const accountingEnsuredRef = useRef(false);

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    try {
      if (!accountingEnsuredRef.current) {
        await axios.post(apiUrl("/accounts/ensure"), {}, authConfig);
        accountingEnsuredRef.current = true;
      }
      const [summaryRes] = await Promise.all([
        axios.get(
          apiUrl(`/accounts/summary?dimension=${activeDimension}`),
          authConfig
        ),
      ]);

      setSummary(normalizeArray(summaryRes.data));
      setExpandedRow("");
      setDetailLoadingRows({});
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to load product financials");
    } finally {
      setLoading(false);
    }
  }, [activeDimension, authConfig, token]);

  const loadRowDetails = useCallback(async (row) => {
    const key = rowIdentity(row);
    if (!key || asArray(row.purchase_details).length || asArray(row.purchase_extra_details).length || asArray(row.inventory_extra_details).length) {
      return;
    }

    setDetailLoadingRows((current) => ({ ...current, [key]: true }));
    try {
      const response = await axios.get(
        apiUrl(
          `/accounts/summary?dimension=${activeDimension}&includeDetails=true&key=${encodeURIComponent(key)}`
        ),
        authConfig
      );
      const detailedRow = normalizeArray(response.data).find((item) => rowIdentity(item) === key);
      if (detailedRow) {
        setSummary((rows) =>
          rows.map((item) => (rowIdentity(item) === key ? { ...item, ...detailedRow } : item))
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to load row cost details");
    } finally {
      setDetailLoadingRows((current) => ({ ...current, [key]: false }));
    }
  }, [activeDimension, authConfig]);

  const toggleRow = (row) => {
    const key = rowIdentity(row);
    setExpandedRow((current) => (current === key ? "" : key));
    if (expandedRow !== key) {
      loadRowDetails(row);
    }
  };

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const totals = useMemo(
    () =>
      summary.reduce(
        (acc, row) => {
          acc.revenue += Number(row.revenue || 0);
          acc.cost += Number(row.final_cost || 0);
          acc.profit += Number(row.profit_loss || 0);
          acc.target += dpricePerPiece(row) * Number(row.no_of_units || 0);
          return acc;
        },
        { revenue: 0, cost: 0, profit: 0, target: 0 }
      ),
    [summary]
  );

  return (
    <StockManagerLayout>
      <main className="space-y-5">
        <section className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Product Pack Financials
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Review product, pack, purchase, batch, supplier, outlet, and
                ecommerce profit/loss.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={loadAll}
                disabled={loading}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <RefreshCw size={16} />
                {loading ? "Loading" : "Refresh"}
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {error}
          </div>
        ) : null}

        <section className="grid gap-3 md:grid-cols-4">
          <StatCard
            label="Revenue"
            value={money(totals.revenue)}
            detail="From mapped sales"
            icon={Calculator}
          />
          <StatCard
            label="Final Cost"
            value={money(totals.cost)}
            detail="Purchase + inventory + channel costs"
            icon={Layers}
          />
          <StatCard
            label="Profit / Loss"
            value={money(totals.profit)}
            detail={totals.profit >= 0 ? "Positive margin" : "Loss"}
            icon={PackageSearch}
          />
          <StatCard
            label="D.Price Target"
            value={money(totals.target)}
            detail="Final cost + configured margin"
            icon={Settings2}
          />
        </section>

        <section className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="flex gap-2 overflow-x-auto">
            {dimensions.map((dimension) => (
              <button
                key={dimension.key}
                type="button"
                onClick={() => setActiveDimension(dimension.key)}
                className={`h-10 flex-none rounded-lg px-4 text-sm font-bold ${
                  activeDimension === dimension.key
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {dimension.label}
              </button>
            ))}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="border-b px-4 py-3">
            <h2 className="text-base font-bold text-gray-900">
              {dimensions.find((item) => item.key === activeDimension)?.label} Profit/Loss
            </h2>
          </div>
          <div className="max-h-[520px] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-slate-100 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">Purchase</th>
                  <th className="px-3 py-2 text-right">Extra Cost</th>
                  <th className="px-3 py-2 text-right">Final Cost</th>
                  <th className="px-3 py-2 text-right">Cost / Piece</th>
                  <th className="px-3 py-2 text-right">MRP / Piece</th>
                  <th className="px-3 py-2 text-right">D.Price / Piece</th>
                  <th className="px-3 py-2 text-right">Discount %</th>
                  <th className="px-3 py-2 text-right">Config Margin</th>
                  <th className="px-3 py-2 text-right">Final Margin</th>
                  <th className="px-3 py-2 text-right">MRP Total</th>
                  <th className="px-3 py-2 text-right">Sold</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Profit / Loss</th>
                  <th className="px-3 py-2 text-right">Actual Margin</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15" className="px-3 py-8 text-center">
                      Loading summary...
                    </td>
                  </tr>
                ) : summary.length ? (
                  summary.map((row) => {
                    const rowKey = rowIdentity(row);
                    const extraCost =
                      Number(row.purchase_extra_cost || 0) +
                      Number(row.inventory_extra_cost || 0);
                    const profit = Number(row.profit_loss || 0);
                    const rowCostPerPiece = costPerPiece(row);
                    const rowPricePerPiece = pricePerPiece(row);
                    const rowFinalPricePerPiece = dpricePerPiece(row);
                    const rowMrpTotal = rowPricePerPiece * Number(row.no_of_units || 0);
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className="cursor-pointer border-t hover:bg-blue-50"
                          onClick={() => toggleRow(row)}
                        >
                          <td className="px-3 py-2 font-semibold text-gray-900">
                            {productPackDisplayName(row)}
                            <div className="text-xs font-normal text-gray-500">
                              {rowContextText(row) || "Click for dependent parameters"}
                            </div>
                            <div className="text-xs font-normal text-gray-500">
                              Click for dependent parameters
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {money(row.purchase_cost)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {money(extraCost)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold">
                            {money(row.final_cost)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {rowCostPerPiece ? wholeMoney(rowCostPerPiece) : "-"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {rowPricePerPiece ? wholeMoney(rowPricePerPiece) : "-"}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-gray-900">
                            {rowFinalPricePerPiece ? wholeMoney(rowFinalPricePerPiece) : "-"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {percent(row.configured_discount_percent)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {percent(row.configured_margin_percent)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {percent(row.rounded_margin_percent)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {money(rowMrpTotal)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {quantity(row.quantity_sold)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {money(row.revenue)}
                          </td>
                          <td
                            title={resultLabel(row)}
                            className={`px-3 py-2 text-right font-bold ${
                              profit >= 0 ? "text-green-700" : "text-red-700"
                            }`}
                          >
                            {money(profit)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {percent(row.profit_loss_percent)}
                          </td>
                        </tr>
                        {expandedRow === rowKey ? (
                          <tr className="border-t bg-slate-50">
                            <td colSpan="15" className="p-0">
                              <FinancialRowDetails
                                row={row}
                                extraCost={extraCost}
                                profit={profit}
                                loadingDetails={Boolean(detailLoadingRows[rowKey])}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="15" className="px-3 py-8 text-center text-gray-500">
                      No financial rows yet. Add cost breakups after
                      purchase/inventory setup.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

      </main>
    </StockManagerLayout>
  );
}
